import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';
import LanguageToggle from '@/components/shared/LanguageToggle';
import { useT } from '@/i18n';

const Login = () => {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Login is NOT geo-fenced — location is only checked at task submission
    // time (user must be in the store then), so no geolocation grab here.
    try {
      await login(email, password);
      toast.success(t('Login successful!'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1769107805465-bfd41863f1a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBicmlnaHQlMjBjbG90aGluZyUyMHN0b3JlJTIwaW50ZXJpb3IlMjBtaW5pbWFsfGVufDB8fHx8MTc3MDk1NDI1OXww&ixlib=rb-4.1.0&q=85"
          alt="Store background"
          className="w-full h-full object-cover opacity-10"
        />
      </div>
      
      <Card className="w-full max-w-md relative z-10 shadow-lg border-border">
        <div className="absolute top-3 right-3"><LanguageToggle /></div>
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>V+</span>
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight">VALUE PLUS</CardTitle>
          <CardDescription className="text-base">{t('Store Manager Reporting System')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t('Login ID')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="text"
                  placeholder={t('Email or Login ID')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">{t('Password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder={t('Enter your password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              className="w-full h-11 btn-primary"
              disabled={loading}
            >
              {loading ? t('Signing in...') : t('Sign In')}
            </Button>
          </form>
          <div className="mt-6 p-4 bg-secondary rounded-md">
            <p className="text-xs text-muted-foreground text-center">
              {t('Contact your administrator for login credentials')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
